<template>
    <div>
        <div v-if="loading" class="text-center" style="padding: 40px 0;">
            <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
        </div>
        <div v-else>
            <div v-if="solunarData" class="card w-100 margin-botton-20">
                <div class="card-body event-card-bg">
                    <div class="mb-2">
                        <div class="form-check form-check-inline">
                            <input class="form-check-input" type="radio" id="solPad30"  :value="30"  v-model="solunarPad">
                            <label class="form-check-label" for="solPad30">±30 min</label>
                        </div>
                        <div class="form-check form-check-inline">
                            <input class="form-check-input" type="radio" id="solPad60"  :value="60"  v-model="solunarPad">
                            <label class="form-check-label" for="solPad60">±60 min</label>
                        </div>
                        <div class="form-check form-check-inline">
                            <input class="form-check-input" type="radio" id="solPad90"  :value="90"  v-model="solunarPad">
                            <label class="form-check-label" for="solPad90">±90 min</label>
                        </div>
                        <div class="form-check form-check-inline">
                            <input class="form-check-input" type="radio" id="solPad120" :value="120" v-model="solunarPad">
                            <label class="form-check-label" for="solPad120">±120 min</label>
                        </div>
                    </div>
                    <div class="mb-3" style="border-radius:8px; overflow:hidden;">
                        <SolunarChart
                            :solunar-data="solunarData"
                            :sunrise-min="solunarSunriseMin"
                            :sunset-min="solunarSunsetMin"
                            :pad="solunarPad"
                            title="Solunar Calendar"
                            :subtitle="chartSubtitle"
                            :subtitle2="chartSubtitle2"
                        />
                    </div>
                    <div class="d-flex align-items-center mb-3 flex-wrap gap-2">
                        <span class="badge" :class="'solunar-badge-' + solunarData.solunar_rating">
                            {{ solunarData.solunar_rating.charAt(0).toUpperCase() + solunarData.solunar_rating.slice(1) }}
                        </span>
                        <span>{{ solunarData.moon_phase_symbol }} {{ solunarData.moon_phase_name }}</span>
                        <span class="text-muted" style="font-size: 0.9em;">{{ solunarData.illumination_percentage }}% illuminated</span>
                    </div>
                    <p v-if="solunarNextPeriod" class="mb-3">
                        <strong>Next:</strong>
                        <span v-if="solunarNextPeriod.type === 'major'" class="solunar-major">★ Major</span>
                        <span v-else class="text-muted">○ Minor</span>
                        — {{ solunarNextPeriod.label }} at {{ solunarNextPeriod.time }}
                    </p>
                    <div v-if="solunarWindowTables.morning.length || solunarWindowTables.evening.length" class="row g-3 mb-4 mt-2">
                        <div v-if="solunarWindowTables.morning.length" class="col-md-6">
                            <h6 class="mb-2" style="font-size:0.85em; color:rgba(0,0,0,0.5); text-transform:uppercase; letter-spacing:.05em;">Morning</h6>
                            <div class="table-responsive">
                                <table class="table table-sm mb-0">
                                    <thead><tr><th>Object</th><th>Begin</th><th>End</th><th>Duration</th></tr></thead>
                                    <tbody>
                                        <tr v-for="(row, i) in solunarWindowTables.morning" :key="i">
                                            <td>{{ row.label }}</td>
                                            <td>{{ row.begin }}</td>
                                            <td>{{ row.end }}</td>
                                            <td>{{ row.duration }} min</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div v-if="solunarWindowTables.evening.length" class="col-md-6">
                            <h6 class="mb-2" style="font-size:0.85em; color:rgba(0,0,0,0.5); text-transform:uppercase; letter-spacing:.05em;">Evening</h6>
                            <div class="table-responsive">
                                <table class="table table-sm mb-0">
                                    <thead><tr><th>Object</th><th>Begin</th><th>End</th><th>Duration</th></tr></thead>
                                    <tbody>
                                        <tr v-for="(row, i) in solunarWindowTables.evening" :key="i">
                                            <td>{{ row.label }}</td>
                                            <td>{{ row.begin }}</td>
                                            <td>{{ row.end }}</td>
                                            <td>{{ row.duration }} min</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm mb-0">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Event</th>
                                    <th>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="period in solunarPeriodsWithStatus" :key="period.minute_of_day"
                                    :class="{ 'solunar-active': period.active, 'text-muted': period.past }">
                                    <td :class="period.type === 'major' ? 'solunar-major' : ''">
                                        {{ period.type === 'major' ? '★ Major' : '○ Minor' }}
                                    </td>
                                    <td>{{ period.label }}</td>
                                    <td>{{ period.time }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div v-if="solunarMonthlyRows.length" class="card w-100 mt-3">
                <div class="card-body event-card-bg">
                    <h3 class="h4 card-title mb-3">{{ currentMonthLabel }}</h3>
                    <div class="table-responsive">
                        <table class="table table-sm mb-0">
                            <thead>
                                <tr>
                                    <th>Day</th>
                                    <th>Rating</th>
                                    <th>Phase</th>
                                    <th class="text-end">Illum</th>
                                    <th>Major Windows</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, i) in solunarMonthlyRows" :key="i"
                                    :class="{ 'table-success': date && parseInt(row.day) === date.getDate() }">
                                    <td>{{ row.day }}</td>
                                    <td>
                                        <span class="badge" :class="'solunar-badge-' + row.rating">
                                            {{ row.rating.charAt(0).toUpperCase() + row.rating.slice(1) }}
                                        </span>
                                    </td>
                                    <td>{{ row.phase }}</td>
                                    <td class="text-end">{{ row.illum }}</td>
                                    <td>{{ row.majors }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import astroTabMixin from '@/mixins/astroTab'
import astra from '@/api/astra'
import { minToTimeStr } from '@/utils/astro'
import SolunarChart from './SolunarChart'

export default {
    name: 'TabSolunar',
    mixins: [astroTabMixin],
    components: { SolunarChart },
    props: {
        active: { type: Boolean, default: false },
    },
    data() {
        return {
            loading: true,
            solunarData: null,
            solunarSunriseMin: null,
            solunarSunsetMin: null,
            solunarPad: 30,
            solunarMonthly: [],
        }
    },
    computed: {
        chartSubtitle() {
            if (!this.city || !this.city.name) return ''
            const parts = [this.city.name]
            if (this.city.region_name) parts.push(this.city.region_name)
            return 'near ' + parts.join(', ')
        },
        chartSubtitle2() {
            if (!this.date) return ''
            return 'for ' + this.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        },
        nowMin() {
            if (!this.time) return null
            return this.time.getHours() * 60 + this.time.getMinutes()
        },
        solunarNextPeriod() {
            if (!this.solunarData || !this.solunarData.periods) return null
            const nm = this.nowMin
            if (nm === null) return this.solunarData.periods[0] || null
            const future = this.solunarData.periods
                .filter(p => p.minute_of_day > nm)
                .sort((a, b) => a.minute_of_day - b.minute_of_day)
            return future[0] || this.solunarData.periods[0] || null
        },
        solunarPeriodsWithStatus() {
            if (!this.solunarData || !this.solunarData.periods) return []
            const nm = this.nowMin
            return this.solunarData.periods.map(p => {
                const tolerance = p.type === 'major' ? 60 : 30
                const active = nm !== null && Math.abs(p.minute_of_day - nm) <= tolerance
                const past   = nm !== null && p.minute_of_day + tolerance < nm
                return { ...p, active, past }
            })
        },
        solunarWindowTables() {
            if (!this.solunarData) return { morning: [], evening: [] }
            const PAD = this.solunarPad
            const periods = this.solunarData.periods || []
            const mrEvent = periods.find(p => p.label === 'Moonrise')
            const msEvent = periods.find(p => p.label === 'Moonset')
            const mrMin = mrEvent ? mrEvent.minute_of_day : null
            const msMin = msEvent ? msEvent.minute_of_day : null

            const srWin = this.solunarSunriseMin !== null ? { label: 'Sun',  begin: this.solunarSunriseMin - PAD, end: this.solunarSunriseMin + PAD, center: this.solunarSunriseMin } : null
            const ssWin = this.solunarSunsetMin  !== null ? { label: 'Sun',  begin: this.solunarSunsetMin  - PAD, end: this.solunarSunsetMin  + PAD, center: this.solunarSunsetMin  } : null
            const mrWin = mrMin !== null ? { label: 'Moon', begin: mrMin - PAD, end: mrMin + PAD, center: mrMin } : null
            const msWin = msMin !== null ? { label: 'Moon', begin: msMin - PAD, end: msMin + PAD, center: msMin } : null

            const sunWins  = [srWin, ssWin].filter(Boolean)
            const moonWins = [mrWin, msWin].filter(Boolean)

            const rows = [...sunWins, ...moonWins]
            for (const sw of sunWins) {
                for (const mw of moonWins) {
                    const lo = Math.max(sw.begin, mw.begin)
                    const hi = Math.min(sw.end, mw.end)
                    if (hi > lo) rows.push({ label: 'Both', begin: lo, end: hi, center: (lo + hi) / 2 })
                }
            }

            const fmt = w => ({ label: w.label, begin: minToTimeStr(w.begin), end: minToTimeStr(w.end), duration: w.end - w.begin })
            const morning = rows.filter(w => w.center <  720).sort((a, b) => a.begin - b.begin).map(fmt)
            const evening = rows.filter(w => w.center >= 720).sort((a, b) => a.begin - b.begin).map(fmt)
            return { morning, evening }
        },
        solunarMonthlyRows() {
            if (!this.solunarMonthly.length) return []
            return this.solunarMonthly.map(item => ({
                day:    item.date.split('-')[2],
                date:   item.date,
                rating: item.solunar_rating,
                phase:  (item.moon_phase_symbol ? item.moon_phase_symbol + ' ' : '') + item.moon_phase_name,
                illum:  Number(item.illumination_percentage).toPrecision(3) + '%',
                majors: (item.periods || []).filter(p => p.type === 'major').map(p => p.time).join(', '),
            }))
        },
    },
    watch: {
        active(val) { if (val) this.load() },
        city()      { if (this.active) this.load() },
        date()      { if (this.active) this.load() },
    },
    mounted() {
        if (this.active) this.load()
    },
    methods: {
        async load() {
            if (!this.latitude || !this.longitude || !this.timezone) return
            if (this.apiLocked) return
            this.loading = true
            this.solunarData = null
            this.solunarSunriseMin = null
            this.solunarSunsetMin = null
            this.solunarMonthly = []
            await Promise.allSettled([
                astra.fetchSolunar({ ...this.loc, date1: this.dailyRange.date1 })
                    .then(r => { this.solunarData = r.data })
                    .catch(() => {}),
                astra.fetchSunRiseSet({ ...this.loc, ...this.dailyRange })
                    .then(r => {
                        const toMin = e => { const [,h,m] = e.datetime.match(/T(\d{2}):(\d{2})/); return +h * 60 + +m }
                        const rise = r.data.events.find(e => e.name === 'Sunrise')
                        const set  = r.data.events.find(e => e.name === 'Sunset')
                        this.solunarSunriseMin = rise ? toMin(rise) : null
                        this.solunarSunsetMin  = set  ? toMin(set)  : null
                    })
                    .catch(() => {}),
                astra.fetchSolunar({ ...this.loc, ...this.monthlyRange })
                    .then(r => { this.solunarMonthly = r.data.results || [] })
                    .catch(() => {}),
            ])
            this.loading = false
        },
    },
}
</script>

<style scoped>
.margin-botton-20 { margin-bottom: 20px; }
.table-responsive td,
.table-responsive th { white-space: nowrap; }
.solunar-badge-strong   { background-color: #198754; color: #fff; }
.solunar-badge-moderate { background-color: #ffc107; color: #000; }
.solunar-badge-weak     { background-color: #6c757d; color: #fff; }
.solunar-active         { background-color: rgba(255, 193, 7, 0.2); font-weight: 600; }
.solunar-major          { color: #856404; }
</style>
